import { createEntity, request } from '@luna/api';
import type { SignInfo } from '@shared/core-types';
import { createSharedSlice } from '@luna/state';
import { createDeferredSigning } from '@shared/deferred-signing';

import type { Contact, ContactListFlag } from './ContactDataManagementTypes';
import { getCDMUrl } from './ContactDataManagementApiService';
import { getContactListFlags, transformContactsToNewStructure } from './useContactsService';
import type { NewStructureContact, OldStructureContact } from './ContactAdapterTypes';

export interface QueryAllResponse {
    contacts: NewStructureContact[] | OldStructureContact[];
    flags: ContactListFlag[] | undefined;
}

export const useContactListFlags = createSharedSlice({
    initialState: { flags: undefined as ContactListFlag[] | undefined },
    reducers: {
        setFlags(state, action: { payload: ContactListFlag[] }) {
            state.flags = action.payload;
        },
    },
});

const { useDeferredSigning, deferredSigning } = createDeferredSigning<SignInfo, Contact>();

export const {
    useEntity: useContact,
    useEntities: useContacts,
    useCreateEntity: useCreateContact,
    useUpdateEntity: useUpdateContact,
    useDeleteEntity: useDeleteContact,
    queryAllIfNeeded: queryContactsIfNeeded,
    selectAll: selectContacts,
    invalidate: invalidateContacts,
} = createEntity({
    name: 'contact-data-management/contact',
    withEntityType: {} as Contact,
    conditionQueryById: (id) => !!id,
    payloadCreators: {
        queryAll: async () => {
            const response = await request<QueryAllResponse>(getCDMUrl());
            const contacts = response.contacts ?? [];

            useContactListFlags.dispatchActions.setFlags(getContactListFlags(response.flags));

            return transformContactsToNewStructure(contacts);
        },
        queryById: (id) => request(`${getCDMUrl()}/${id}`),
        create: async (contact) => {
            const { signInfo } = await request.post<Contact>(getCDMUrl(), contact);
            const createdSignedContact = await deferredSigning({ signInfo });
            return createdSignedContact!;
        },
        updateById: async ({ id, changes }) => {
            const { signInfo, ...contact } = await request.put<Contact>(`${getCDMUrl()}/${id}`, {
                id,
                ...changes,
            });
            await deferredSigning({ signInfo, id });
            return contact;
        },
        deleteById: async (id) => {
            const { signInfo } = await request.delete<{ signInfo: SignInfo }>(
                `${getCDMUrl()}/${id}`,
            );
            await deferredSigning({ signInfo, id: id as string });
            return null;
        },
    },
});

export const useContactSigning = useDeferredSigning;
